import type { RosterUser } from '@/types/user';

export const LEADERSHIP_EMAILS = [
  'anthony@lendwisemtg.com',
  'gady@lendwisemtg.com',
  'tony@lendwisemtg.com',
  'david@lendwisemtg.com',
  'denise@lendwisemtg.com',
  'jennifer@lendwisemtg.com',
  'jeremy@lendwisemtg.com',
  'alberto@lendwisemtg.com',
  'kevin@lendwisemtg.com',
  'brandon@lendwisemtg.com'
] as const;

export const ROSTER_TEAMS = [
  'Leadership',
  'Operations',
  'Production'
] as const;

export type RosterTeam = typeof ROSTER_TEAMS[number];
export type RosterTeamFilter = 'All' | RosterTeam;
export type RosterGroups = Record<RosterTeam, RosterUser[]>;

const leadershipRank = new Map<string, number>(
  LEADERSHIP_EMAILS.map((email, index) => [email, index])
);

const TEAM_PRIORITY: Record<string, number> = {
  partner: 1,
  admin: 2,
  manager: 3,
  processor: 4,
  loan_officer_assistant: 5,
  underwriter: 6,
  closer: 7,
  compliance_specialist: 8,
  loan_officer: 9,
  recruiter: 10
};

const DEFAULT_TEAM_PRIORITY = 99;
const DEFAULT_SENIORITY = 10;

const SENIORITY_TIERS: ReadonlyArray<readonly [RegExp, number]> = [
  [/\b(president|ceo)\b/i, 1],
  [/\bvp\b|\bvice\s*president\b/i, 2],
  [/\bdirector\b/i, 3],
  [/\bsr\.?\b|\bsenior\b/i, 4],
  [/\blead\b/i, 5]
];

function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

function getSeniority(title: string | undefined): number {
  if (!title) {
    return DEFAULT_SENIORITY;
  }
  for (const [pattern, rank] of SENIORITY_TIERS) {
    if (pattern.test(title)) {
      return rank;
    }
  }
  return DEFAULT_SENIORITY;
}

function getRolePriority(user: RosterUser): number {
  let best = DEFAULT_TEAM_PRIORITY;
  for (const role of user.roles ?? []) {
    const rank = TEAM_PRIORITY[role];
    if (rank !== undefined && rank < best) {
      best = rank;
    }
  }
  return best;
}

export function hasRealNmlsNumber(
  value: string | null | undefined
): boolean {
  const normalized = (value ?? '').trim();
  const digits = normalized.replace(/\D/g, '');
  return digits.length > 0 && !/^0+$/.test(digits);
}

export function getRosterTeam(user: RosterUser): RosterTeam {
  if (leadershipRank.has(normalizeEmail(user.email))) {
    return 'Leadership';
  }
  if (
    user.roles?.includes('loan_officer')
    && hasRealNmlsNumber(user.nmlsNumber)
  ) {
    return 'Production';
  }
  return 'Operations';
}

/**
 * Preserve the roster's existing role cluster, title seniority, and last-name
 * ordering inside Operations and Production.
 */
export function compareExistingRosterOrder(a: RosterUser, b: RosterUser): number {
  const roleDifference = getRolePriority(a) - getRolePriority(b);
  if (roleDifference !== 0) {
    return roleDifference;
  }

  const seniorityDifference = getSeniority(a.title) - getSeniority(b.title);
  if (seniorityDifference !== 0) {
    return seniorityDifference;
  }

  return (a.lastName || '').localeCompare(b.lastName || '');
}

export function createRosterGroups(users: readonly RosterUser[]): RosterGroups {
  const groups: RosterGroups = {
    Leadership: [],
    Operations: [],
    Production: []
  };

  for (const user of users) {
    if (user.employmentStatus !== 'active') {
      continue;
    }
    groups[getRosterTeam(user)].push(user);
  }

  groups.Leadership.sort((a, b) => (
    (leadershipRank.get(normalizeEmail(a.email)) ?? Number.MAX_SAFE_INTEGER)
    - (leadershipRank.get(normalizeEmail(b.email)) ?? Number.MAX_SAFE_INTEGER)
  ));
  groups.Operations.sort(compareExistingRosterOrder);
  groups.Production.sort(compareExistingRosterOrder);

  return groups;
}

export function flattenRosterGroups(groups: RosterGroups): RosterUser[] {
  return ROSTER_TEAMS.flatMap((team) => groups[team]);
}
